import React from 'react'

import { v4 as uuidv4 } from 'uuid'

import { ContextSchema } from 'features/expressions/types'
import { WorkflowNodeEditorComponent } from 'features/workflows/types'
import { useWorkflowEditorContext } from 'features/workflows/useWorkflowEditorContext'
import { WorkflowConditionInput } from 'features/workflows/WorkflowConditionInput'
import { ConfigurationLabel } from 'features/workflows/WorkflowUI'

import { Box } from 'ui/components/Box'
import { Button } from 'ui/components/Button'
import { Divider } from 'ui/components/Divider'
import { Icon } from 'ui/components/Icon'
import { Input } from 'ui/components/Input'
import { Body } from 'ui/components/Text'

type ConditionalEditorProps = React.ComponentPropsWithoutRef<typeof Box> & {
    node: WorkflowConditionalConfig
}

export const ConditionalEditor: WorkflowNodeEditorComponent<ConditionalEditorProps> = ({
    onChange,
    node,
    props,
}) => {
    const { contextSchema } = useWorkflowEditorContext()

    // We need to ensure there's at least one branch
    const branches = node.branches?.length ? node.branches : []

    const handleAddBranch = () => {
        const branchNumber = branches.length + 1
        const newBranch: WorkflowConditionalBranch = {
            id: uuidv4(),
            kind: 'conditional_branch',
            name: `Condition ${branchNumber}`,
            condition: {
                clauses: [],
                operator: 'AND',
            },
            chain: [],
        }

        onChange({
            branches: [...branches, newBranch],
        } as Partial<WorkflowConditionalConfig>)
    }

    const handleRemoveBranch = (branchId: string) => {
        onChange({
            branches: branches.filter((branch) => branch.id !== branchId),
        } as Partial<WorkflowConditionalConfig>)
    }

    const handleBranchConditionChange = (branchId: string, condition: WorkflowCondition) => {
        onChange({
            branches: branches.map((branch) =>
                branch.id === branchId ? { ...branch, condition } : branch
            ),
        } as Partial<WorkflowConditionalConfig>)
    }

    const handleBranchNameChange = (branchId: string, name: string) => {
        onChange({
            branches: branches.map((branch) =>
                branch.id === branchId ? { ...branch, name } : branch
            ),
        } as Partial<WorkflowConditionalConfig>)
    }

    return (
        <Box flex column stretch p="xl" {...props}>
            <ConfigurationLabel mt="xs">Conditional Branches</ConfigurationLabel>
            <Body color="textWeak" fontSize="bodyS" mb="m">
                Add one or more conditions. The first matching condition will execute its chain.
            </Body>

            {branches.map((branch, index) => (
                <Box
                    key={branch.id}
                    mb="l"
                    p="m"
                    borderWidth={1}
                    borderColor="border"
                    borderRadius="m"
                >
                    <Box flex alignItems="center" justifyContent="space-between" mb="m">
                        <Box flex alignItems="center" flexGrow={1} mr="m">
                            <Body fontWeight="bodyMedium" mr="m">
                                Name:
                            </Body>
                            <Input
                                value={branch.name || `Condition ${index + 1}`}
                                onChange={(e) => handleBranchNameChange(branch.id, e.target.value)}
                                placeholder={`Condition ${index + 1}`}
                                size="m"
                                flexGrow={1}
                            />
                        </Box>
                        {branches.length > 1 && (
                            <Button
                                variant="ghost"
                                size="s"
                                iconOnly
                                onClick={() => handleRemoveBranch(branch.id)}
                                title="Remove branch"
                            >
                                <Icon name="X" size="xs" />
                            </Button>
                        )}
                    </Box>

                    <WorkflowConditionInput
                        value={branch.condition}
                        onChange={(condition) => handleBranchConditionChange(branch.id, condition)}
                        contextSchema={contextSchema as ContextSchema}
                    />
                </Box>
            ))}

            <Button variant="secondary" size="s" onClick={handleAddBranch} mb="l">
                Add Branch
            </Button>

            <Divider my="m" />

            <Body color="textWeak" fontSize="bodyS">
                If no conditions match, the else chain will execute.
            </Body>
        </Box>
    )
}
